import * as THREE from 'three';
import { BLOCKS, HOTBAR_BLOCKS, ALL_PLACEABLE, ALL_ITEMS } from './blocks';
import { Inventory } from './inventory';
import { matchRecipe, getRecipeBook, RECIPES } from './crafting';
import { showCursor, hideCursor } from './client/cursor-manager';
import { t, getBlockName } from './i18n';
import { escapeHtml } from './sanitize';
import { FpsGraph } from './client/fps-graph';

function colorToCSS(color: number): string {
  return '#' + color.toString(16).padStart(6, '0');
}

// --- 3D voxel preview renderer (cached) ---
const previewCache = new Map<number, string>();
let previewRenderer: THREE.WebGLRenderer | null = null;
const previewScene = new THREE.Scene();
const previewCamera = new THREE.OrthographicCamera(-1.2, 1.2, 1.2, -1.2, 0.1, 10);

function initPreviewRenderer() {
  if (previewRenderer) return;
  previewRenderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
  previewRenderer.setSize(64, 64);
  previewRenderer.setClearColor(0x000000, 0);

  previewCamera.position.set(1.5, 1.3, 1.5);
  previewCamera.lookAt(0, 0, 0);

  const ambient = new THREE.AmbientLight(0xffffff, 0.6);
  const directional = new THREE.DirectionalLight(0xffffff, 0.8);
  directional.position.set(2, 3, 1);
  previewScene.add(ambient);
  previewScene.add(directional);
}

function renderBlockPreview(blockId: number): string {
  if (previewCache.has(blockId)) return previewCache.get(blockId)!;

  initPreviewRenderer();
  const block = BLOCKS[blockId];
  if (!block) return '';

  // Clear previous objects (keep lights)
  const toRemove: THREE.Object3D[] = [];
  previewScene.traverse((obj) => {
    if (obj instanceof THREE.Mesh) toRemove.push(obj);
  });
  toRemove.forEach(obj => {
    previewScene.remove(obj);
    (obj as THREE.Mesh).geometry.dispose();
  });

  const topColor = block.topColor || block.color;
  const sideColor = block.color;
  const bottomColor = block.bottomColor || block.color;

  if (block.isItem) {
    // Item shapes: flat or distinct 3D shapes
    let mesh: THREE.Mesh;
    if (blockId === 19) {
      // Stick — tall thin box
      const geo = new THREE.BoxGeometry(0.2, 1.2, 0.2);
      mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: sideColor }));
      mesh.rotation.z = 0.3;
    } else if (blockId === 26 || blockId === 27) {
      // Pickaxe — T shape from two boxes
      const handle = new THREE.BoxGeometry(0.15, 1.0, 0.15);
      const head = new THREE.BoxGeometry(0.7, 0.2, 0.15);
      const handleMesh = new THREE.Mesh(handle, new THREE.MeshLambertMaterial({ color: 0x8b6c3a }));
      const headMesh = new THREE.Mesh(head, new THREE.MeshLambertMaterial({ color: sideColor }));
      handleMesh.position.y = -0.1;
      headMesh.position.y = 0.5;
      const group = new THREE.Group();
      group.add(handleMesh);
      group.add(headMesh);
      group.rotation.z = 0.3;
      previewScene.add(group);
      previewRenderer!.render(previewScene, previewCamera);
      const url = previewRenderer!.domElement.toDataURL();
      previewCache.set(blockId, url);
      previewScene.remove(group);
      handleMesh.geometry.dispose();
      headMesh.geometry.dispose();
      return url;
    } else {
      // Generic item — small cube
      const geo = new THREE.BoxGeometry(0.6, 0.6, 0.6);
      mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: sideColor }));
    }
    previewScene.add(mesh);
    previewRenderer!.render(previewScene, previewCamera);
    const url = previewRenderer!.domElement.toDataURL();
    previewCache.set(blockId, url);
    previewScene.remove(mesh);
    mesh.geometry.dispose();
    return url;
  }

  // Non-solid decorative blocks — smaller shape
  if (!block.solid) {
    let mesh: THREE.Mesh;
    if (blockId === 13 || blockId === 32 || blockId === 33) {
      // Flower / mushroom / tall grass — cross-shaped billboard
      const geo = new THREE.BoxGeometry(0.5, 0.8, 0.1);
      mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: sideColor }));
    } else if (blockId === 20) {
      // Torch — thin tall yellow-topped stick
      const stickGeo = new THREE.BoxGeometry(0.15, 0.7, 0.15);
      const stickMesh = new THREE.Mesh(stickGeo, new THREE.MeshLambertMaterial({ color: 0x8b6c3a }));
      const flameGeo = new THREE.BoxGeometry(0.2, 0.25, 0.2);
      const flameMesh = new THREE.Mesh(flameGeo, new THREE.MeshLambertMaterial({ color: 0xffcc00, emissive: 0xff8800, emissiveIntensity: 0.5 }));
      stickMesh.position.y = -0.15;
      flameMesh.position.y = 0.35;
      previewScene.add(stickMesh);
      previewScene.add(flameMesh);
      previewRenderer!.render(previewScene, previewCamera);
      const url = previewRenderer!.domElement.toDataURL();
      previewCache.set(blockId, url);
      previewScene.remove(stickMesh);
      previewScene.remove(flameMesh);
      stickGeo.dispose();
      flameGeo.dispose();
      return url;
    } else {
      // Generic non-solid — flat panel
      const geo = new THREE.BoxGeometry(0.8, 0.8, 0.15);
      mesh = new THREE.Mesh(geo, new THREE.MeshLambertMaterial({ color: sideColor, transparent: block.transparent, opacity: 0.8 }));
    }
    previewScene.add(mesh);
    previewRenderer!.render(previewScene, previewCamera);
    const url = previewRenderer!.domElement.toDataURL();
    previewCache.set(blockId, url);
    previewScene.remove(mesh);
    mesh.geometry.dispose();
    return url;
  }

  // Solid blocks — isometric cube with per-face colors
  const topMat = new THREE.MeshLambertMaterial({ color: topColor });
  const bottomMat = new THREE.MeshLambertMaterial({ color: bottomColor });
  const sideMat = new THREE.MeshLambertMaterial({
    color: sideColor,
    transparent: block.transparent,
    opacity: block.transparent ? 0.8 : 1.0,
  });
  const materials = [sideMat, sideMat, topMat, bottomMat, sideMat, sideMat];
  const geo = new THREE.BoxGeometry(1, 1, 1);
  const mesh = new THREE.Mesh(geo, materials);
  mesh.rotation.y = Math.PI / 6;

  previewScene.add(mesh);
  previewRenderer!.render(previewScene, previewCamera);
  const url = previewRenderer!.domElement.toDataURL();
  previewCache.set(blockId, url);
  previewScene.remove(mesh);
  geo.dispose();
  topMat.dispose();
  bottomMat.dispose();
  sideMat.dispose();

  return url;
}

function createBlockPreview(blockId: number): HTMLDivElement {
  const preview = document.createElement('div');
  preview.style.width = '32px';
  preview.style.height = '32px';
  preview.style.flexShrink = '0';

  const dataUrl = renderBlockPreview(blockId);
  if (dataUrl) {
    const img = document.createElement('img');
    img.src = dataUrl;
    img.style.width = '100%';
    img.style.height = '100%';
    img.style.imageRendering = 'pixelated';
    preview.appendChild(img);
  } else {
    const block = BLOCKS[blockId];
    if (block) {
      preview.style.backgroundColor = colorToCSS(block.color);
    }
  }

  preview.style.border = '1px solid rgba(255,255,255,0.2)';
  preview.style.borderRadius = '2px';
  return preview;
}

export type CraftingMode = 'none' | 'inventory' | 'crafting';

const HOTBAR_STORAGE_KEY = 'jannecraft-hotbar';

export class HUD {
  private selectedSlot: number = 0;
  private hotbarSlots: HTMLDivElement[] = [];
  private hotbarBlockIds: number[] = [...HOTBAR_BLOCKS];
  private chatMessages: { el: HTMLDivElement; timer: ReturnType<typeof setTimeout> }[] = [];

  private inventory: Inventory;
  private craftingMode: CraftingMode = 'none';
  private craftingGrid: number[][] = [[0,0,0],[0,0,0],[0,0,0]];
  private craftingTab: 'craft' | 'recipes' = 'craft';
  private helpOpen = false;
  private helpTab: 'resources' | 'controls' | 'recipes' = 'resources';
  private sleepOverlay: HTMLDivElement | null = null;
  private sleepVisible = false;
  private fpsGraph: FpsGraph | null = null;
  private playerListOverlay: HTMLDivElement | null = null;
  private playerListVisible = false;

  // Callbacks
  onCraftingModeChange?: (mode: CraftingMode) => void;

  constructor(inventory: Inventory) {
    this.inventory = inventory;
    this.loadHotbar();
    this.buildHotbar();

    const fpsContainer = document.getElementById('fps-graph-container');
    if (fpsContainer) {
      this.fpsGraph = new FpsGraph(fpsContainer);
    }
  }

  private loadHotbar(): void {
    try {
      const raw = localStorage.getItem(HOTBAR_STORAGE_KEY);
      if (raw) {
        const ids = JSON.parse(raw);
        if (Array.isArray(ids) && ids.length === 9) {
          this.hotbarBlockIds = ids;
        }
      }
    } catch {}
  }

  private saveHotbar(): void {
    localStorage.setItem(HOTBAR_STORAGE_KEY, JSON.stringify(this.hotbarBlockIds));
  }

  private buildHotbar(): void {
    const hotbar = document.getElementById('hotbar');
    if (!hotbar) return;

    hotbar.innerHTML = '';
    this.hotbarSlots = [];

    for (let i = 0; i < 9; i++) {
      const slot = document.createElement('div');
      slot.className = 'hotbar-slot' + (i === this.selectedSlot ? ' selected' : '');

      const slotNumber = document.createElement('span');
      slotNumber.className = 'slot-number';
      slotNumber.textContent = String(i + 1);
      slot.appendChild(slotNumber);

      const blockId = this.hotbarBlockIds[i];
      const block = BLOCKS[blockId];
      const count = this.inventory.getCount(blockId);

      if (count > 0 && block) {
        const preview = createBlockPreview(blockId);
        const countEl = document.createElement('span');
        countEl.className = 'slot-count';
        countEl.textContent = String(count);

        const name = document.createElement('span');
        name.className = 'block-name';
        name.textContent = getBlockName(blockId);

        slot.appendChild(preview);
        slot.appendChild(countEl);
        slot.appendChild(name);
      }

      hotbar.appendChild(slot);
      this.hotbarSlots.push(slot);
    }
  }

  selectSlot(n: number): void {
    if (n < 0 || n > 8) return;
    this.hotbarSlots[this.selectedSlot]?.classList.remove('selected');
    this.selectedSlot = n;
    this.hotbarSlots[this.selectedSlot]?.classList.add('selected');
  }

  getSelectedBlock(): number {
    return this.hotbarBlockIds[this.selectedSlot];
  }

  getHotbarBlockIds(): number[] {
    return [...this.hotbarBlockIds];
  }

  setHotbarBlockIds(ids: number[]): void {
    this.hotbarBlockIds = [...ids];
    this.saveHotbar();
    this.refreshHotbar();
  }

  getInventory(): Inventory {
    return this.inventory;
  }

  getCraftingMode(): CraftingMode {
    return this.craftingMode;
  }

  isUIOpen(): boolean {
    return this.craftingMode !== 'none' || this.helpOpen;
  }

  refreshHotbar(): void {
    for (let i = 0; i < 9; i++) {
      this.rebuildSlot(i);
    }
  }

  update(player: { position: THREE.Vector3 }, dt: number): void {
    if (this.fpsGraph) {
      this.fpsGraph.update(dt);
    }

    const posEl = document.getElementById('pos');
    if (posEl) {
      const p = player.position;
      posEl.textContent = t('hud.position', { x: Math.floor(p.x), y: Math.floor(p.y), z: Math.floor(p.z) });
    }

    // Refresh hotbar counts periodically
    this.refreshHotbar();
  }

  // --- UI toggling ---

  openCrafting(mode: CraftingMode): void {
    this.closeCrafting(); // close any existing
    this.craftingMode = mode;
    showCursor('crafting');
    this.craftingGrid = [[0,0,0],[0,0,0],[0,0,0]];

    const screen = document.getElementById('crafting-screen');
    if (!screen) return;

    screen.classList.remove('hidden');
    this.renderCraftingUI();
    this.onCraftingModeChange?.(mode);
  }

  closeCrafting(): void {
    // Return items from crafting grid to inventory
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        if (this.craftingGrid[r][c] !== 0) {
          this.inventory.add(this.craftingGrid[r][c]);
          this.craftingGrid[r][c] = 0;
        }
      }
    }
    this.craftingMode = 'none';
    this.closeHelp();
    hideCursor('crafting');
    const screen = document.getElementById('crafting-screen');
    if (screen) screen.classList.add('hidden');
    this.onCraftingModeChange?.('none');
  }

  toggleInventory(): void {
    if (this.craftingMode !== 'none') {
      this.closeCrafting();
      return;
    }
    this.openCrafting('inventory');
  }

  toggleCrafting(): void {
    if (this.craftingMode !== 'none') {
      this.closeCrafting();
      return;
    }
    this.openCrafting('crafting');
  }

  private renderCraftingUI(): void {
    const screen = document.getElementById('crafting-screen');
    if (!screen) return;

    screen.innerHTML = '';

    const title = document.createElement('h2');

    if (this.craftingMode === 'inventory') {
      title.textContent = t('crafting.inventory');
      screen.appendChild(title);
      const hint = document.createElement('p');
      hint.textContent = t('crafting.hint', { slot: this.selectedSlot + 1 });
      hint.style.fontSize = '12px';
      hint.style.color = '#aaa';
      hint.style.marginBottom = '8px';
      hint.style.textAlign = 'center';
      screen.appendChild(hint);
      this.renderInventoryGrid(screen);
      return;
    }

    title.textContent = t('crafting.crafting');
    screen.appendChild(title);

    // Tab bar
    const tabBar = document.createElement('div');
    tabBar.className = 'craft-tabs';

    const craftTab = document.createElement('button');
    craftTab.className = 'craft-tab' + (this.craftingTab === 'craft' ? ' active' : '');
    craftTab.textContent = t('crafting.crafting');
    craftTab.addEventListener('click', () => {
      this.craftingTab = 'craft';
      this.renderCraftingUI();
    });

    const recipesTab = document.createElement('button');
    recipesTab.className = 'craft-tab' + (this.craftingTab === 'recipes' ? ' active' : '');
    recipesTab.textContent = t('crafting.recipeBook');
    recipesTab.addEventListener('click', () => {
      this.craftingTab = 'recipes';
      this.renderCraftingUI();
    });

    tabBar.appendChild(craftTab);
    tabBar.appendChild(recipesTab);
    screen.appendChild(tabBar);

    if (this.craftingTab === 'craft') {
      const craftArea = document.createElement('div');
      craftArea.className = 'craft-area';

      // Grid (always 3x3)
      const gridDiv = document.createElement('div');
      gridDiv.className = 'craft-grid';
      gridDiv.style.gridTemplateColumns = `repeat(3, 50px)`;

      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          const slot = document.createElement('div');
          slot.className = 'craft-slot';
          const itemId = this.craftingGrid[r][c];
          if (itemId !== 0) {
            const preview = createBlockPreview(itemId);
            const nameEl = document.createElement('span');
            nameEl.className = 'craft-slot-name';
            nameEl.textContent = getBlockName(itemId);
            slot.appendChild(preview);
            slot.appendChild(nameEl);
            // Click to remove from grid
            slot.addEventListener('click', () => {
              this.inventory.add(itemId);
              this.craftingGrid[r][c] = 0;
              this.renderCraftingUI();
            });
          } else {
            slot.textContent = '';
          }
          gridDiv.appendChild(slot);
        }
      }

      craftArea.appendChild(gridDiv);

      // Arrow
      const arrow = document.createElement('div');
      arrow.className = 'craft-arrow';
      arrow.textContent = '→';
      craftArea.appendChild(arrow);

      // Output slot
      const result = matchRecipe(this.craftingGrid);
      const outputSlot = document.createElement('div');
      outputSlot.className = 'craft-output';

      if (result) {
        const preview = createBlockPreview(result.itemId);
        const countEl = document.createElement('span');
        countEl.className = 'craft-output-count';
        countEl.textContent = `x${result.count}`;
        const nameEl = document.createElement('span');
        nameEl.className = 'craft-slot-name';
        nameEl.textContent = getBlockName(result.itemId);
        outputSlot.appendChild(preview);
        outputSlot.appendChild(countEl);
        outputSlot.appendChild(nameEl);

        // Click output to craft
        outputSlot.addEventListener('click', () => {
          this.doCraft(result);
        });
      }

      craftArea.appendChild(outputSlot);
      screen.appendChild(craftArea);

      // Inventory below
      this.renderInventoryGrid(screen);
    } else {
      // Recipes tab
      this.renderRecipeBook(screen);

      // Inventory below
      this.renderInventoryGrid(screen);
    }
  }

  private renderRecipeBook(screen: HTMLElement): void {

    const bookGrid = document.createElement('div');
    bookGrid.className = 'recipe-book';

    const inventoryMap = this.inventory.getAll();
    const recipes = getRecipeBook(inventoryMap);

    for (const { recipe, canCraft, ingredients } of recipes) {
      const recipeCard = document.createElement('div');
      recipeCard.className = 'recipe-card' + (canCraft ? ' craftable' : ' missing');

      // Result
      const resultRow = document.createElement('div');
      resultRow.className = 'recipe-result';

      const resultPreview = createBlockPreview(recipe.result);
      const resultName = document.createElement('span');
      resultName.className = 'recipe-result-name';
      resultName.textContent = `${getBlockName(recipe.result)} x${recipe.count}`;

      resultRow.appendChild(resultPreview);
      resultRow.appendChild(resultName);
      recipeCard.appendChild(resultRow);

      // Ingredients
      const ingredientsRow = document.createElement('div');
      ingredientsRow.className = 'recipe-ingredients';

      // Count ingredients needed
      const needed = new Map<number, number>();
      for (const row of recipe.pattern) {
        for (const cell of row) {
          if (cell !== 0) {
            needed.set(cell, (needed.get(cell) || 0) + 1);
          }
        }
      }

      for (const [itemId, count] of needed) {
        const block = BLOCKS[itemId];
        const have = inventoryMap.get(itemId) || 0;
        const hasEnough = have >= count;

        const ingredientEl = document.createElement('span');
        ingredientEl.className = 'recipe-ingredient' + (hasEnough ? ' has-enough' : ' not-enough');

        const miniPreview = document.createElement('div');
        miniPreview.className = 'recipe-ingredient-preview';
        miniPreview.style.backgroundColor = colorToCSS(block?.color || 0);

        const text = document.createElement('span');
        text.textContent = `${count}x ${getBlockName(itemId)}`;

        ingredientEl.appendChild(miniPreview);
        ingredientEl.appendChild(text);
        ingredientsRow.appendChild(ingredientEl);
      }

      recipeCard.appendChild(ingredientsRow);

      // Click recipe card to auto-fill crafting grid and switch to craft tab
      if (canCraft) {
        recipeCard.addEventListener('click', () => {
          this.craftingTab = 'craft';
          this.fillRecipe(recipe);
        });
      }

      bookGrid.appendChild(recipeCard);
    }

    screen.appendChild(bookGrid);
  }

  private renderInventoryGrid(screen: HTMLElement): void {
    const invTitle = document.createElement('h3');
    invTitle.textContent = t('crafting.items');
    invTitle.style.marginTop = '16px';
    screen.appendChild(invTitle);

    const grid = document.createElement('div');
    grid.className = 'inventory-grid';

    const allItems = this.inventory.getAll();

    // Only show items the player actually has
    const sortedIds = [...allItems.keys()].filter(id => id !== 0 && allItems.get(id)! > 0).sort((a, b) => a - b);

    for (const itemId of sortedIds) {
      const count = this.inventory.getCount(itemId);
      const block = BLOCKS[itemId];
      if (!block) continue;

      const slot = document.createElement('div');
      slot.className = 'inventory-slot';

      const preview = createBlockPreview(itemId);

      const countEl = document.createElement('span');
      countEl.className = 'slot-count';
      countEl.textContent = count > 0 ? String(count) : '0';

      const name = document.createElement('span');
      name.className = 'block-name';
      name.textContent = getBlockName(itemId);

      slot.appendChild(preview);
      slot.appendChild(countEl);
      slot.appendChild(name);

      if (count > 0) {
        slot.addEventListener('click', () => {
          if (this.craftingMode === 'crafting') {
            this.addToCraftingGrid(itemId);
          } else if (this.craftingMode === 'inventory') {
            // Click in inventory mode → assign to hotbar
            this.hotbarBlockIds[this.selectedSlot] = itemId;
            this.saveHotbar();
            this.refreshHotbar();
            this.renderCraftingUI();
          }
        });
      }

      grid.appendChild(slot);
    }

    screen.appendChild(grid);
  }

  private addToCraftingGrid(itemId: number): void {
    // Find first empty slot in 3x3 grid
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        if (this.craftingGrid[r][c] === 0) {
          if (this.inventory.remove(itemId)) {
            this.craftingGrid[r][c] = itemId;
            this.renderCraftingUI();
          }
          return;
        }
      }
    }
  }

  private fillRecipe(recipe: import('./crafting').Recipe): void {
    // Return current grid items to inventory
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        if (this.craftingGrid[r][c] !== 0) {
          this.inventory.add(this.craftingGrid[r][c]);
          this.craftingGrid[r][c] = 0;
        }
      }
    }

    const pH = recipe.pattern.length;
    const pW = recipe.pattern[0].length;

    // Remove ingredients from inventory and fill grid
    const itemsToReturn: number[] = [];
    let success = true;

    for (let r = 0; r < pH && success; r++) {
      for (let c = 0; c < pW && success; c++) {
        const itemId = recipe.pattern[r][c];
        if (itemId === 0) continue;
        if (this.inventory.remove(itemId)) {
          this.craftingGrid[r][c] = itemId;
          itemsToReturn.push(itemId);
        } else {
          success = false;
        }
      }
    }

    // If we failed partway, return everything
    if (!success) {
      for (let r = 0; r < 3; r++) {
        for (let c = 0; c < 3; c++) {
          if (this.craftingGrid[r][c] !== 0) {
            this.inventory.add(this.craftingGrid[r][c]);
            this.craftingGrid[r][c] = 0;
          }
        }
      }
    }

    this.renderCraftingUI();
  }

  private doCraft(result: { itemId: number; count: number }): void {
    // Clear the grid (items consumed)
    for (let r = 0; r < 3; r++) {
      for (let c = 0; c < 3; c++) {
        this.craftingGrid[r][c] = 0;
      }
    }
    // Add result to inventory
    this.inventory.add(result.itemId, result.count);
    this.renderCraftingUI();
    this.refreshHotbar();
  }

  private rebuildSlot(index: number): void {
    const slot = this.hotbarSlots[index];
    if (!slot) return;

    const blockId = this.hotbarBlockIds[index];
    const block = BLOCKS[blockId];
    const count = this.inventory.getCount(blockId);

    slot.innerHTML = '';

    const slotNumber = document.createElement('span');
    slotNumber.className = 'slot-number';
    slotNumber.textContent = String(index + 1);
    slot.appendChild(slotNumber);

    // Only show item if player has it
    if (count > 0 && block) {
      const preview = createBlockPreview(blockId);
      const countEl = document.createElement('span');
      countEl.className = 'slot-count';
      countEl.textContent = String(count);

      const name = document.createElement('span');
      name.className = 'block-name';
      name.textContent = getBlockName(blockId);

      slot.appendChild(preview);
      slot.appendChild(countEl);
      slot.appendChild(name);
    }
  }

  addChatMessage(name: string, message: string): void {
    const chat = document.getElementById('chat');
    if (!chat) return;

    const msgEl = document.createElement('div');
    msgEl.className = 'chat-message';
    msgEl.innerHTML = `<b>${escapeHtml(name)}</b>: ${escapeHtml(message)}`;

    chat.appendChild(msgEl);

    const timer = setTimeout(() => {
      this.removeChatMessage(entry);
    }, 10000);

    const entry = { el: msgEl, timer };
    this.chatMessages.push(entry);

    while (this.chatMessages.length > 5) {
      const oldest = this.chatMessages.shift()!;
      clearTimeout(oldest.timer);
      oldest.el.remove();
    }
  }

  private removeChatMessage(entry: { el: HTMLDivElement; timer: ReturnType<typeof setTimeout> }): void {
    entry.el.remove();
    const idx = this.chatMessages.indexOf(entry);
    if (idx !== -1) {
      this.chatMessages.splice(idx, 1);
    }
  }

  // --- Sleep overlay ---

  showSleepOverlay(sleepingCount: number, totalCount: number, onWakeUp: () => void): void {
    if (!this.sleepOverlay) {
      this.sleepOverlay = document.createElement('div');
      this.sleepOverlay.id = 'sleep-overlay';
      this.sleepOverlay.style.cssText = `
        position: fixed; inset: 0; z-index: 9000;
        background: rgba(0, 0, 20, 0.75);
        display: flex; flex-direction: column;
        align-items: center; justify-content: center;
        font-family: 'Courier New', monospace; color: #fff;
        cursor: pointer; user-select: none; pointer-events: auto;
      `;
      document.body.appendChild(this.sleepOverlay);
    }

    this.sleepOverlay.innerHTML = '';

    const title = document.createElement('div');
    title.textContent = t('sleep.sleeping');
    title.style.cssText = 'font-size: 32px; font-weight: bold; margin-bottom: 12px; color: #aaccff;';
    this.sleepOverlay.appendChild(title);

    const count = document.createElement('div');
    count.textContent = t('sleep.playerCount', { sleeping: String(sleepingCount), total: String(totalCount) });
    count.style.cssText = 'font-size: 18px; margin-bottom: 24px; color: #88aadd;';
    this.sleepOverlay.appendChild(count);

    const hint = document.createElement('div');
    hint.textContent = t('sleep.wakeHint');
    hint.style.cssText = 'font-size: 14px; color: #6688aa; animation: pulse 2s infinite;';
    this.sleepOverlay.appendChild(hint);

    this.sleepOverlay.style.display = 'flex';
    this.sleepVisible = true;

    this.sleepOverlay.onclick = () => {
      onWakeUp();
    };
  }

  hideSleepOverlay(): void {
    if (this.sleepOverlay) {
      this.sleepOverlay.style.display = 'none';
      this.sleepVisible = false;
    }
  }

  isSleepOverlayVisible(): boolean {
    return this.sleepVisible;
  }

  // --- Player list overlay (Tab hold) ---

  showPlayerList(localName: string, localPos: { x: number; y: number; z: number }, localYaw: number, remotePlayers: { id: number; name: string; x: number; y: number; z: number }[]): void {
    if (!this.playerListOverlay) {
      this.playerListOverlay = document.createElement('div');
      this.playerListOverlay.id = 'player-list-overlay';
      document.body.appendChild(this.playerListOverlay);
    }

    this.playerListOverlay.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'player-list-title';
    title.textContent = t('playerList.title');
    this.playerListOverlay.appendChild(title);

    const table = document.createElement('div');
    table.className = 'player-list-table';

    // Header row
    const header = document.createElement('div');
    header.className = 'player-list-row player-list-header';
    header.innerHTML = `<span class="player-list-name">${escapeHtml(t('playerList.name'))}</span><span class="player-list-dir"></span><span class="player-list-pos">${escapeHtml(t('playerList.position'))}</span>`;
    table.appendChild(header);

    // Local player (you) first
    const localRow = document.createElement('div');
    localRow.className = 'player-list-row player-list-self';
    localRow.innerHTML = `<span class="player-list-name">${escapeHtml(localName)} <span class="player-list-you">(${escapeHtml(t('playerList.you'))})</span></span><span class="player-list-dir"></span><span class="player-list-pos">${Math.floor(localPos.x)}, ${Math.floor(localPos.y)}, ${Math.floor(localPos.z)}</span>`;
    table.appendChild(localRow);

    // Remote players with direction arrow
    for (const p of remotePlayers) {
      const dx = p.x - localPos.x;
      const dz = p.z - localPos.z;
      const dist = Math.floor(Math.sqrt(dx * dx + dz * dz));
      // Angle from local player to remote, relative to player's facing direction
      // yaw 0 = looking toward -Z; atan2 gives angle from +X axis
      const worldAngle = Math.atan2(dx, -dz);
      const relAngle = worldAngle - localYaw;
      // Unicode arrow rotated via CSS transform
      const arrowDeg = (relAngle * 180 / Math.PI);

      const row = document.createElement('div');
      row.className = 'player-list-row';
      row.innerHTML = `<span class="player-list-name">${escapeHtml(p.name)}</span><span class="player-list-dir"><span class="player-list-arrow" style="transform:rotate(${arrowDeg.toFixed(0)}deg)">\u2191</span> <span class="player-list-dist">${dist}m</span></span><span class="player-list-pos">${p.x}, ${p.y}, ${p.z}</span>`;
      table.appendChild(row);
    }

    const count = document.createElement('div');
    count.className = 'player-list-count';
    count.textContent = t('playerList.count', { count: remotePlayers.length + 1 });
    this.playerListOverlay.appendChild(count);

    this.playerListOverlay.appendChild(table);

    this.playerListOverlay.style.display = 'flex';
    this.playerListVisible = true;
  }

  hidePlayerList(): void {
    if (this.playerListOverlay) {
      this.playerListOverlay.style.display = 'none';
      this.playerListVisible = false;
    }
  }

  isPlayerListVisible(): boolean {
    return this.playerListVisible;
  }

  // --- Help screen ---

  isHelpOpen(): boolean {
    return this.helpOpen;
  }

  toggleHelp(): void {
    if (this.helpOpen) {
      this.closeHelp();
    } else {
      this.openHelp();
    }
  }

  openHelp(): void {
    this.helpOpen = true;
    showCursor('help');
    this.renderHelp();
  }

  closeHelp(): void {
    this.helpOpen = false;
    hideCursor('help');
    const screen = document.getElementById('help-screen');
    if (screen) screen.classList.add('hidden');
  }

  private renderHelp(): void {
    const screen = document.getElementById('help-screen');
    if (!screen) return;

    screen.classList.remove('hidden');
    screen.innerHTML = '';

    const title = document.createElement('h2');
    title.textContent = t('help.title');
    screen.appendChild(title);

    // Tab bar
    const tabBar = document.createElement('div');
    tabBar.className = 'craft-tabs';

    const tabs: { id: typeof this.helpTab; label: string }[] = [
      { id: 'resources', label: t('help.resources') },
      { id: 'controls', label: t('help.controls') },
      { id: 'recipes', label: t('help.recipes') },
    ];

    for (const tab of tabs) {
      const btn = document.createElement('button');
      btn.className = 'craft-tab' + (this.helpTab === tab.id ? ' active' : '');
      btn.textContent = tab.label;
      btn.addEventListener('click', () => {
        this.helpTab = tab.id;
        this.renderHelp();
      });
      tabBar.appendChild(btn);
    }
    screen.appendChild(tabBar);

    if (this.helpTab === 'resources') {
      this.renderHelpResources(screen);
    } else if (this.helpTab === 'controls') {
      this.renderHelpControls(screen);
    } else {
      this.renderHelpRecipes(screen);
    }

    // Close hint
    const hint = document.createElement('div');
    hint.className = 'help-hint';
    hint.textContent = t('help.closeHint');
    screen.appendChild(hint);
  }

  private renderHelpResources(screen: HTMLElement): void {
    const sections: { title: string; items: { blockId: number; desc: string }[] }[] = [
      {
        title: t('help.ores'),
        items: [
          { blockId: 14, desc: t('help.coal') },
          { blockId: 15, desc: t('help.iron') },
          { blockId: 16, desc: t('help.gold') },
          { blockId: 17, desc: t('help.diamond') },
          { blockId: 42, desc: t('help.obsidian') },
        ],
      },
      {
        title: t('help.biomes'),
        items: [
          { blockId: 1, desc: t('help.forest') },
          { blockId: 4, desc: t('help.desert') },
          { blockId: 12, desc: t('help.tundra') },
          { blockId: 38, desc: t('help.swamp') },
          { blockId: 3, desc: t('help.mountains') },
        ],
      },
      {
        title: t('help.special'),
        items: [
          { blockId: 44, desc: t('help.flyblock') },
          { blockId: 45, desc: t('help.superflyblock') },
          { blockId: 46, desc: t('help.portal') },
          { blockId: 48, desc: t('help.bed') },
          { blockId: 63, desc: t('help.speedblock') },
          { blockId: 30, desc: t('help.ice') },
          { blockId: 55, desc: t('help.arrowblock') },
          { blockId: 50, desc: t('help.launchpad') },
          { blockId: 51, desc: t('help.paintblocks') },
          { blockId: 59, desc: t('help.musicblock') },
          { blockId: 62, desc: t('help.firework') },
          { blockId: 60, desc: t('help.teleportblock') },
          { blockId: 61, desc: t('help.tnt') },
          { blockId: 40, desc: t('help.lava') },
          { blockId: 7, desc: t('help.water') },
        ],
      },
    ];

    for (const section of sections) {
      const heading = document.createElement('h3');
      heading.textContent = section.title;
      heading.style.marginTop = '12px';
      screen.appendChild(heading);

      const list = document.createElement('div');
      list.className = 'help-list';

      for (const item of section.items) {
        const row = document.createElement('div');
        row.className = 'help-row';

        const preview = createBlockPreview(item.blockId);
        const name = document.createElement('span');
        name.className = 'help-block-name';
        name.textContent = getBlockName(item.blockId);
        const desc = document.createElement('span');
        desc.className = 'help-desc';
        desc.textContent = item.desc;

        row.appendChild(preview);
        row.appendChild(name);
        row.appendChild(desc);
        list.appendChild(row);
      }

      screen.appendChild(list);
    }
  }

  private renderHelpControls(screen: HTMLElement): void {
    const controls: { key: string; action: string }[] = [
      { key: 'WASD', action: t('controls.move') },
      { key: t('help.keySpace'), action: t('controls.jump') },
      { key: 'Shift', action: t('controls.sprint') },
      { key: t('help.keyMouse'), action: t('controls.look') },
      { key: 'LMB', action: t('controls.break') },
      { key: 'RMB', action: t('controls.place') },
      { key: '1-9', action: t('controls.selectBlock') },
      { key: 'E', action: t('controls.inventory') },
      { key: 'C', action: t('controls.craft') },
      { key: 'H', action: t('help.helpKey') },
      { key: 'T', action: t('controls.chat') },
      { key: 'M', action: t('controls.mute') },
      { key: 'L', action: t('controls.lighting') },
      { key: 'Tab', action: t('controls.playerList') },
      { key: 'Esc', action: t('help.close') },
    ];

    const list = document.createElement('div');
    list.className = 'help-controls-list';

    for (const ctrl of controls) {
      const row = document.createElement('div');
      row.className = 'help-control-row';

      const key = document.createElement('span');
      key.className = 'help-key';
      key.textContent = ctrl.key;

      const action = document.createElement('span');
      action.className = 'help-action';
      action.textContent = ctrl.action;

      row.appendChild(key);
      row.appendChild(action);
      list.appendChild(row);
    }

    screen.appendChild(list);
  }

  private renderHelpRecipes(screen: HTMLElement): void {
    const list = document.createElement('div');
    list.className = 'help-recipes-list';

    for (const recipe of RECIPES) {
      const row = document.createElement('div');
      row.className = 'help-recipe-row';

      // Result
      const resultPreview = createBlockPreview(recipe.result);
      const resultName = document.createElement('span');
      resultName.className = 'help-recipe-name';
      resultName.textContent = `${getBlockName(recipe.result)} x${recipe.count}`;

      // Ingredients
      const needed = new Map<number, number>();
      for (const r of recipe.pattern) {
        for (const cell of r) {
          if (cell !== 0) needed.set(cell, (needed.get(cell) || 0) + 1);
        }
      }

      const ingredients = document.createElement('span');
      ingredients.className = 'help-recipe-ingredients';
      const parts: string[] = [];
      for (const [itemId, count] of needed) {
        parts.push(`${count}x ${getBlockName(itemId)}`);
      }
      ingredients.textContent = parts.join(' + ');

      row.appendChild(resultPreview);
      row.appendChild(resultName);

      const arrow = document.createElement('span');
      arrow.className = 'help-recipe-arrow';
      arrow.textContent = '\u2190';
      row.appendChild(arrow);
      row.appendChild(ingredients);

      list.appendChild(row);
    }

    screen.appendChild(list);
  }
}
